import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { GRUPA_PO_SLUGU, LITERY, PATENT_PO_SLUGU, kluczeOdpowiedzi, patentZDzialem } from '../data/patenty';
import { pobierzPytanie, pytanieZPamieci } from '../data/pytania';
import MarkdownRenderer from './MarkdownRenderer';
import JsonLd from '../seo/JsonLd';
import { absoluteUrl, breadcrumbJsonLd, faqJsonLd } from '../seo/siteMeta';
import styles from '../css/modules/Nauka.module.css';
import { podstawaPrawna } from '../lib/podstawa';

/* Tytuł i opis strony pytania.
 *
 * Część pytań to całe scenariusze nawigacyjne - najdłuższy dawał 353 znaki
 * w <title>. Wyszukiwarka pokazuje około 60, więc taki tytuł i tak zostaje
 * ucięty, a w wynikach widać sam początek scenariusza, bez informacji, czego
 * strona dotyczy. Skracamy na granicy słowa i dopisujemy krótszy ogon, gdy samo
 * pytanie zjada limit.
 */
const LIMIT_TYTULU = 65;

/* Skracamy z zachowaniem KOŃCÓWKI pytania, nie samego początku.
 *
 * Pytania chodzą parami różniącymi się jednym słowem na końcu ("...wzdłuż
 * twojej lewej burty" / "...prawej burty"). Ucinanie po limicie dawało obu
 * identyczny tytuł - w audycie 3.09.2026 wyszło pięć takich par. Dlatego
 * zostawiamy początek, wielokropek i ostatnie słowa, które je rozróżniają. */
const OGON_SLOW = 3;
/* Ogon nie może zjeść tytułu - przy dłuższym zostawiamy samo skrócenie z przodu. */
const UDZIAL_OGONA = 2.5;

const skroc = (tekst, limit) => {
  const jedna = (tekst || '').replace(/\s+/g, ' ').trim();
  if (jedna.length <= limit) return jedna;

  const doGranicy = (tresc, ile) => {
    const ciete = tresc.slice(0, ile);
    const spacja = ciete.lastIndexOf(' ');
    const baza = spacja > ile / 2 ? ciete.slice(0, spacja) : ciete;
    return baza.replace(/[\s,;:.-]+$/, '');
  };

  const slowa = jedna.replace(/[?:.]+$/, '').split(' ');
  const ogon = slowa.slice(-OGON_SLOW).join(' ');
  // Ogon ma mieścić się w rozsądnej części limitu, inaczej z tytułu zostaje
  // sama końcówka i nie wiadomo, o co pytanie.
  if (ogon.length <= limit / UDZIAL_OGONA) {
    const glowa = doGranicy(jedna, limit - ogon.length - 2);
    if (glowa && !ogon.startsWith(glowa)) return `${glowa}… ${ogon}`;
  }
  return `${doGranicy(jedna, limit)}…`;
};

export const tytulPytania = tekst => {
  // Dwukropek kończący pytanie przed myślnikiem czytałby się jak usterka
  // ("Kambuz to: - odpowiedź").
  const pytanie = (tekst || '').replace(/\s+/g, ' ').trim().replace(/:$/, '');
  const ogon = ' - odpowiedź i wyjaśnienie';
  if (pytanie.length + ogon.length <= LIMIT_TYTULU) return pytanie + ogon;

  const krotki = ' - odpowiedź';
  return skroc(pytanie, LIMIT_TYTULU - krotki.length) + krotki;
};

/**
 * Strona pojedynczego pytania: /pytania/<slug>.
 *
 * To główne źródło ruchu long-tail - ktoś wpisuje w wyszukiwarkę treść pytania
 * i trafia na stronę z odpowiedzią, omówieniem i podstawą prawną. Dlatego
 * treść musi być w HTML już po prerenderze, a nie dopiero po hydratacji.
 */
const QuestionPage = () => {
  const { slug } = useParams();
  /* Jeżeli dział jest już w pamięci (index.js dociąga go przed montowaniem),
     startujemy od razu z treścią. Inaczej pierwszy render byłby krótszym stanem
     "wczytujemy", a podmiana prerenderowanego HTML-u dałaby skok układu. */
  const [pytanie, setPytanie] = useState(() => pytanieZPamieci(slug));
  const [status, setStatus] = useState(() => (pytanieZPamieci(slug) ? 'ok' : 'ladowanie'));

  useEffect(() => {
    let anulowane = false;
    const zPamieci = pytanieZPamieci(slug);
    setPytanie(zPamieci);
    setStatus(zPamieci ? 'ok' : 'ladowanie');
    if (zPamieci) return undefined;

    // Treść bierzemy z lokalnych plików generowanych przez scripts/pytania/gen.py,
    // a nie z API. Prerender zrzuca DOM po budżecie czasu wirtualnego, który nie
    // czeka na odpowiedź z zewnętrznego hosta - przy fetchu strony pytań
    // zapisywały się puste. Dział dociąga się osobnym plikiem, więc odwiedzający
    // pobiera tylko swój fragment bazy.
    pobierzPytanie(slug)
      .then(d => {
        if (anulowane) return;
        if (!d) {
          setStatus('brak');
          return;
        }
        setPytanie(d);
        setStatus('ok');
      })
      .catch(() => !anulowane && setStatus('brak'));

    return () => {
      anulowane = true;
    };
  }, [slug]);

  if (status === 'ladowanie') {
    return <div className={styles.wrap}><p className={styles.info}>Wczytujemy pytanie…</p></div>;
  }

  if (status === 'brak' || !pytanie) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>Nie znaleźliśmy tego pytania</h1>
        <p className={styles.info}>Możliwe, że adres jest nieaktualny.</p>
        <Link className={styles.cta} to="/">Wróć na stronę główną</Link>
      </div>
    );
  }

  const klucze = kluczeOdpowiedzi(pytanie);
  const grupa = GRUPA_PO_SLUGU[pytanie.grupa];
  const patenty = (pytanie.patent || []).map(p => PATENT_PO_SLUGU[p]).filter(Boolean);
  /* Do działu linkujemy przez patent, który TEN dział w ogóle ma. Sternik
     motorowodny nie ma teorii żeglowania, więc branie zawsze patenty[0]
     wysyłało dziesięć stron pytań prosto na 404. */
  const patentDzialu = grupa ? patentZDzialem(patenty, grupa.slug) : null;
  const poprawna = pytanie[pytanie.correctAnswer];

  const tytul = tytulPytania(pytanie.question);
  // Opis w wynikach wyszukiwania i tak urywa się przy ~160 znakach, więc
  // przycinamy go na granicy słowa zamiast zostawiać zdanie ucięte w połowie.
  const opis = skroc(
    `${pytanie.question} Poprawna odpowiedź: ${poprawna}. ${pytanie.explanation || ''}`,
    160,
  );

  return (
    <div className={styles.wrap}>
      <Helmet>
        <title>{tytul}</title>
        <meta name="description" content={opis} />
        <link rel="canonical" href={absoluteUrl(`/pytania/${slug}`)} />
      </Helmet>

      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        ...(patenty[0] ? [{ name: patenty[0].nazwa, path: `/${patenty[0].slug}` }] : []),
        { name: 'Pytanie', path: `/pytania/${slug}` },
      ])} />
      <JsonLd data={faqJsonLd([{ question: pytanie.question, answer: `${poprawna}. ${pytanie.explanation || ''}`.trim() }])} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link>
        {patenty[0] && <> / <Link to={`/${patenty[0].slug}`}>{patenty[0].nazwa}</Link></>}
        {grupa && patentDzialu && <> / <Link to={`/nauka/${patentDzialu.slug}/${grupa.slug}`}>{grupa.nazwa}</Link></>}
        {grupa && !patentDzialu && <> / {grupa.nazwa}</>}
      </nav>

      <h1 className={styles.h1}>{pytanie.question}</h1>

      <div className={styles.karta}>
        <div className={styles.odpowiedzi}>
          {klucze.map(k => (
            <div key={k} className={`${styles.odpowiedz} ${k === pytanie.correctAnswer ? styles.dobra : ''}`}>
              <span className={styles.litera}>{LITERY[k]}</span>
              <span>{pytanie[k]}</span>
            </div>
          ))}
        </div>

        <div className={styles.omowienie}>
          <p className={styles.werdyktOk}>Poprawna odpowiedź: {LITERY[pytanie.correctAnswer]}</p>
          {pytanie.explanation && <MarkdownRenderer content={pytanie.explanation} />}
          {podstawaPrawna(pytanie) && (
            <p className={styles.podstawa}>Podstawa prawna: {podstawaPrawna(pytanie)}</p>
          )}

          {/* Arkusze PZŻ są publikowane bez klucza odpowiedzi. Tam, gdzie odpowiedź
              ustaliliśmy sami, mówimy o tym wprost - użytkownik ma wiedzieć, kiedy
              warto potwierdzić ją u instruktora. */}
          {pytanie.zrodlo_odpowiedzi === 'model' && (
            <p className={styles.zastrzezenie}>
              Oficjalny arkusz egzaminacyjny dla tego patentu jest publikowany bez klucza
              odpowiedzi. Powyższą odpowiedź ustaliliśmy na podstawie przepisów i praktyki
              morskiej, a nie z klucza wydanego przez organizatora egzaminu. Jeżeli masz inne
              zdanie, napisz do nas - sprawdzimy i poprawimy.
            </p>
          )}
        </div>
      </div>

      <div className={styles.tresc}>
        <h2 className={styles.h2}>Gdzie to pytanie się pojawia</h2>
        <p>
          {grupa ? `Dział: ${grupa.nazwa}. ` : ''}
          {patenty.length
            ? `Obowiązuje na egzaminie na ${patenty.map(p => `patent ${p.dopelniacz}`).join(' oraz ')}.`
            : ''}
        </p>
      </div>

      <div className={styles.akcje}>
        {patenty[0] && (
          <Link className={styles.cta} to={`/egzamin/${patenty[0].slug}`}>
            Rozwiąż egzamin próbny
          </Link>
        )}
        {grupa && patentDzialu && (
          <Link className={styles.ctaDrugi} to={`/nauka/${patentDzialu.slug}/${grupa.slug}`}>
            Więcej pytań z działu {grupa.nazwa.toLowerCase()}
          </Link>
        )}
      </div>
    </div>
  );
};

export default QuestionPage;
